import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api, clientId } from "@/app/api/client"

// Computed on read by LeaseStatusCalculator — never persisted (see AGENTS.md's
// "domain math is computed live"). Mirrors Api::V1::LeasesController#serialize.
export type LeaseCategory = "expiring" | "active" | "expired" | "hbp" | "nodate"

export interface LeaseSource {
  id: string
  assignor: string | null
  assignee: string | null
  book: string | null
  page: string | null
  recorded_on: string | null
  filename: string | null
}

// A single Master Lease Schedule row. `status`/`expiration`/`category` are
// computed server-side; everything else is a stored column.
export interface Lease {
  id: string
  lessor: string
  lessee: string | null
  lease_date: string | null
  term_years: number | null
  extension_years: number | null
  extension_exercised_on: string | null
  book: string | null
  page: string | null
  county: string
  str: string | null
  legal: string | null
  flags: string[]
  origin: "app" | "import"
  status: string
  expiration: string | null
  category: LeaseCategory
  sources: LeaseSource[]
}

// GET /leases returns a plain array (no {data, meta} envelope — unlike
// transactions/wells), so useLeases hands back Lease[] directly.
export interface LeaseFilters {
  q?: string
  strs?: string[]
  category?: LeaseCategory | ""
}

export interface LeasePayload {
  lessor: string
  lessee?: string | null
  lease_date?: string | null
  term_years?: number | null
  extension_years?: number | null
  extension_exercised_on?: string | null
  book?: string | null
  page?: string | null
  county: string
  str?: string | null
  legal?: string | null
  flags?: string[]
}

function buildQuery(filters: LeaseFilters): string {
  const params = new URLSearchParams()
  if (filters.q) params.set("q", filters.q)
  for (const s of filters.strs ?? []) params.append("strs[]", s)
  if (filters.category) params.set("category", filters.category)
  const qs = params.toString()
  return qs ? `?${qs}` : ""
}

export function useLeases(filters: LeaseFilters = {}, opts: { enabled?: boolean } = {}) {
  return useQuery<Lease[]>({
    queryKey: ["leases", clientId, filters],
    queryFn: () => api.get<Lease[]>(`/leases${buildQuery(filters)}`),
    enabled: opts.enabled,
  })
}

export function useCreateLease() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: LeasePayload) => api.post<Lease>("/leases", payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["leases"] }),
  })
}

export function useUpdateLease() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: LeasePayload }) =>
      api.patch<Lease>(`/leases/${id}`, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["leases"] }),
  })
}

export function useDeleteLease() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.del(`/leases/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["leases"] }),
  })
}
